import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { RequestService } from '../request/request';
import { Group, GroupDTO } from '../../models/group.interface';
import { Profile, ProfileRoleDTO, ProfileSectorDTO } from '../../models/users.interface';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';
import { CustomRoute, routeList } from '../../../app.routes';
import { Pagination } from '../../models/pagination.interface';
import { QueryGet } from '../../types/query-types';
import {
  Permision,
  PermisionDTO,
  PermissionActions,
  PermissionVerb,
} from '../../models/permision.interface';
import { AsyncFormValidation } from '../../interceptors/error.interceptor';

@Injectable({
  providedIn: 'root',
})
export class GroupService {
  private apiUrl = environment.service_1 + 'auth_user/groups';
  private url = environment.service_1 + 'auth_user/permision';
  private urlPath = environment.service_1 + 'auth_user/assign-user-sector/';

  public asyncErrorValidation = new BehaviorSubject<AsyncFormValidation | null>(null);
  asyncErrorValidation$ = this.asyncErrorValidation.asObservable();

  constructor(
    private http: HttpClient,
    private requestService: RequestService,
  ) {}

  httpOptions = {
    headers: new HttpHeaders({}),
  };

  private gruposeleccionado = new BehaviorSubject<Group | null>(null);
  gruposeleccionado$ = this.gruposeleccionado.asObservable();

  private grupos = new BehaviorSubject<Group[]>([]);
  grupos$ = this.grupos.asObservable();

  setGrupo(grupo: Group) {
    this.gruposeleccionado.next(grupo);
  }
  setGrupos(grupos: Group[]) {
    this.grupos.next(grupos);
  }

  getGroups(query: QueryGet): Observable<Pagination<Group[]>> {
    return this.requestService.get<Group[]>(this.apiUrl, query);
  }

  getGrupos(): Observable<Group[]> {
    return this.http.get<Group[]>(this.apiUrl + '/all/', {
      headers: this.httpOptions.headers,
    });
  }

  addGroup(group: GroupDTO) {
    return this.http.post(this.apiUrl + '/add/', group).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          this.asyncErrorValidation.next(err.error);
        },
      }),
    );
  }

  editGroup(group: Group) {
    return this.http.put(`${this.apiUrl}/edit/${group.id}`, group).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          this.asyncErrorValidation.next(err.error);
        },
      }),
    );
  }

  assignPermisions(permisions: Permision[]) {
    return this.http.post(`${this.apiUrl}permision/assign`, permisions);
  }

  assignSector(payload: ProfileSectorDTO) {
    return this.http.post(this.urlPath, payload);
  }

  assignGroup(payload: ProfileRoleDTO) {
    return this.http.post(`${this.apiUrl}/assign`, payload);
  }

  revokePermision(group: Group) {
    return this.http.post(`${this.url}/revoke/${group.id}`, group);
  }

  addPermision(path: string, action: PermissionVerb, moduleName: string) {
    const name = path.length > 0 ? path : 'root';
    const newPermision: PermisionDTO = {
      name: `${name}${moduleName} (${action})`,
      path: `/${path}`,
      role_group: [],
      user: [],
      create: false,
      update: false,
      delete: false,
      read: true,
    };
    const payload = { ...newPermision, [action]: true };
    return this.http.post(this.url + '/add', payload);
  }

  getPermisions() {
    return this.http.get<Pagination<Permision[]>>(this.apiUrl + '/permision');
  }

  getAllPermisions(canWrite: boolean) {
    return this.http.get<Permision[]>(this.url + `/all?canWrite=${canWrite}`);
  }

  /**
   * extract persmision groups in a new list
   * @param {Group[]} groups
   * @returns {Permision[]}
   */
  getGroupPermisions(groups: Group[]): Permision[] {
    return groups.flatMap((group: Group) => group.permisions.map((el: Permision) => el));
  }

  hasSpecialAccess(profile: Profile | null): boolean {
    if (profile) {
      // the profile fileds is_superuser and is_stafff can use to skip the regular role validation
      if (profile && profile.is_superuser) {
        return true;
      }
      // if (profile && profile.is_staff) {
      //     return true
      // }
    }

    return false;
  }

  hasPermissionAccess(
    permissions: Permision[],
    target: string,
    action: PermissionActions,
  ): boolean {
    if (permissions.length === 0) {
      return false;
    }

    const permissionMap = {
      r: 'read',
      u: 'update',
      c: 'create',
      d: 'delete',
    };

    if (!(action in permissionMap)) {
      return false;
    }

    const canAccess = permissions.some((perm) => {
      const performAction = permissionMap[action];
      const canPerformAction =
        performAction in perm ? (perm[performAction as keyof Permision] as boolean) : false;
      return perm.path === target && canPerformAction;
    });

    return canAccess;
  }

  canPerform(target: string, action: PermissionActions, profile: Profile | null): boolean {
    if (!profile) {
      return false;
    }

    if (this.hasSpecialAccess(profile)) {
      return true;
    }

    const uPermisions = profile ? profile.permisions : [];
    const userCanWrite = this.hasPermissionAccess(uPermisions, target, action);

    if (userCanWrite) {
      return true;
    }

    //role/group valiation
    const gPermisions = profile ? this.getGroupPermisions(profile.role_group) : [];
    const groupCanWrite = this.hasPermissionAccess(gPermisions, target, action);
    if (profile && groupCanWrite) {
      return true;
    }

    return false;
  }

  canAccess(target: string, profile: Profile | null): boolean {
    if (!profile) {
      return false;
    }

    if (this.hasSpecialAccess(profile)) {
      return true;
    }

    const routePath = this.hasRoutePath(target);

    //user validation
    const uPermisions = profile ? profile.permisions : [];
    const userCanAccess = uPermisions.some(
      (perm) => perm.path === target || perm.path === routePath,
    );
    if (profile && userCanAccess) {
      return true;
    }

    //role/group valiation
    const gPermisions = profile ? this.getGroupPermisions(profile.role_group) : [];
    const groupCanAccess = gPermisions.some(
      (perm) => perm.path === target || perm.path === routePath,
    );

    if (profile && groupCanAccess) {
      return true;
    }

    return false;
  }

  generatePermisions() {
    routeList.forEach((route: CustomRoute) => {
      const path = route.path || 'root';
      const isModule = route.data.isModule ? '_module' : '';
      this.addPermision(path, 'read', isModule)
        .pipe(
          switchMap(() => {
            return this.addPermision(path, 'update', isModule);
          }),
          switchMap(() => {
            return this.addPermision(path, 'create', isModule);
          }),
          switchMap(() => {
            return this.addPermision(path, 'delete', isModule);
          }),
        )
        .subscribe();
    });
    alert('Permisos generados correctamente');
  }

  /** detects and return route path by @author chatuz */
  hasRoutePath(target: string) {
    const hasPathRegex = /^\/[a-z\/\-]+\/\d+$/;
    const textPathRegex = /\/([a-z\-]+)\//;
    const textPath = target.match(textPathRegex);

    if (hasPathRegex.test(target) && textPath) {
      return `${textPath[0]}:id`;
    }
    return null;
  }

  getFirstGroup(user: Profile | null): string {
    if (user && this.hasSpecialAccess(user)) {
      // use reserved word on future to get all if is admin
      return 'admin';
    }
    if (user) {
      const firstGroup = user.role_group.length > 0 ? user.role_group[0].name : 'public';
      return firstGroup;
    }
    return 'public';
  }

  assignUserPermision(permisions: Permision[], user: Profile) {
    return this.http.post(`${this.url}/user/${user.id}`, permisions);
  }
}
